import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './BrandOnboardingEditor.scss';

import Tooltip from '../General/Tooltip.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';

import { getSmartImage } from '../../helpers/helpers.js';
import { CodeChip } from './BrandOnboardingChips';
import { copyToClipboard } from './brandOnboardingHelpers';
import {
  CLOSED_PROGRAM_FORCED_VALUES,
  COPYABLE_READONLY_FIELDS,
  DAY_FIELDS,
  FIELD_SECTIONS,
  HALF_WIDTH_FIELDS,
  NETWORK_LABELS,
  PERCENT_FIELDS,
  URL_READONLY_FIELDS
} from './brandOnboardingFieldConfig';

const BrandOnboardingEditor = props => {
  const { fields, isFieldEditable, onFieldChange, collapsedSectionTitles, onToggleSectionCollapsed, recipientNamesById } = props;
  const { header, children, actions } = props;

  const sectionedFieldNames = new Set(FIELD_SECTIONS.flatMap(section => section.fieldNames));
  const fieldSections = FIELD_SECTIONS.map(section => ({
    title: section.title,
    collapsible: section.collapsible,
    fields: section.fieldNames
      .map(fieldName => {
        const index = fields.findIndex(f => f.name === fieldName);
        return index >= 0 ? { field: fields[index], index } : null;
      })
      .filter(Boolean)
  }));
  const unsectionedFields = fields.map((field, index) => ({ field, index })).filter(({ field }) => !sectionedFieldNames.has(field.name));
  if (unsectionedFields.length) fieldSections.push({ title: 'Other', fields: unsectionedFields });

  const isClosedProgramChecked = !!fields.find(f => f.name === 'isClosedProgram')?.value;
  const fieldIsLockedByClosedProgram = field => isClosedProgramChecked && field.name in CLOSED_PROGRAM_FORCED_VALUES;

  const renderField = (field, index) => {
    if (field.type === 'checkbox') {
      return (
        <div
          key={field.name}
          className={cn('brand-onboarding-toggle-row', { on: !!field.value })}
          onClick={() => onFieldChange(index, f => ({ value: !f.value }))}
        >
          <div className='brand-onboarding-toggle-row-label'>
            {field.label}
            {field.tooltip && (
              <span onClick={e => e.stopPropagation()}>
                <Tooltip message={field.tooltip}>
                  <FontAwesomeIcon icon={faInfoCircle} className='brand-onboarding-result-input-container-label-tooltip-icon' />
                </Tooltip>
              </span>
            )}
          </div>
          <div className='brand-onboarding-toggle-switch' />
        </div>
      );
    }

    return (
      <div key={field.name} className={cn('brand-onboarding-result-input-container', { half: HALF_WIDTH_FIELDS.has(field.name) })}>
        <div className='brand-onboarding-result-input-container-label'>
          {field.label}
          {field.tooltip && (
            <Tooltip message={field.tooltip}>
              <FontAwesomeIcon icon={faInfoCircle} className='brand-onboarding-result-input-container-label-tooltip-icon' />
            </Tooltip>
          )}
        </div>
        {(() => {
          const inputElement = field.customComponent ? (
            field.customComponent(field, index, onFieldChange)
          ) : (
            <input
              className={cn('brand-onboarding-result-input-container-input', {
                invalid: !field.isValid,
                alignRight: field.inputValueAlignRight,
                filled: !!field.value
              })}
              type={field.type || 'text'}
              placeholder={field.placeholder || field.label}
              id={field.name}
              value={field.value}
              onChange={e => {
                const { value } = e.target;
                onFieldChange(index, { value: field.sanitize ? field.sanitize(value) : value });
              }}
            />
          );
          // react-tippy only registers tooltips whose message is non-empty at mount — wrap only when invalid.
          return field.isValid ? inputElement : <Tooltip message={field.errorMessage}>{inputElement}</Tooltip>;
        })()}

        {!!field.supportText && (
          <div className={cn('brand-onboarding-input-support-text', { alignRight: !field.inputValueAlignRight })}>{field.supportText}</div>
        )}
      </div>
    );
  };

  const renderReadOnlyValue = field => {
    if (field.type === 'checkbox') return field.value ? 'Yes' : 'No';
    if (field.name === 'NotificationsUser_id') {
      if (!field.value) return '—';
      return recipientNamesById.get(field.value) || `User ${field.value}`;
    }
    if (field.value === '' || field.value == null) return '—';
    if (PERCENT_FIELDS.has(field.name)) return `${field.value}%`;
    if (DAY_FIELDS.has(field.name)) return `${field.value} days`;
    if (field.name === 'force_merchant') return NETWORK_LABELS[field.value] || String(field.value);
    return String(field.value);
  };

  const renderReadOnlyField = field => {
    const isUrlField = URL_READONLY_FIELDS.has(field.name);
    const rawValue = renderReadOnlyValue(field);
    // Stored upload urls point at the private S3 bucket — surface the CloudFront url people can actually open.
    const value = isUrlField && rawValue !== '—' ? getSmartImage(rawValue) : rawValue;
    const isCopyable = COPYABLE_READONLY_FIELDS.has(field.name) && value !== '—';
    const isOpenableUrl = isUrlField && /^https?:\/\//.test(value);
    const copyValue = isUrlField ? value : field.value;
    return (
      <div key={field.name} className='brand-onboarding-readonly-row'>
        <div className='brand-onboarding-readonly-row-label'>{field.label.replace(/\*$/, '')}</div>
        <div className='brand-onboarding-readonly-row-value' title={value}>
          {isCopyable ? <CodeChip code={value} href={isOpenableUrl ? value : undefined} onCopy={() => copyToClipboard(copyValue)} /> : value}
        </div>
      </div>
    );
  };

  return (
    <div className='brand-onboarding-editor-outer-container'>
      <div className='brand-onboarding-editor-inner-container'>
        {header}
        {children}

        {fieldSections.map(section => {
          const isCollapsed = section.collapsible && collapsedSectionTitles.has(section.title);
          return (
            <div className='brand-onboarding-section' key={section.title}>
              <div
                className={cn('brand-onboarding-section-title', { collapsible: section.collapsible, collapsed: isCollapsed })}
                onClick={section.collapsible ? () => onToggleSectionCollapsed(section.title) : undefined}
              >
                {section.title}
                {section.collapsible && <FontAwesomeIcon icon={faAngleDown} className='brand-onboarding-section-title-chevron' />}
              </div>
              {!isCollapsed && (
                <div className='brand-onboarding-section-fields'>
                  {section.fields.map(({ field, index }) =>
                    isFieldEditable(field) && !fieldIsLockedByClosedProgram(field) ? renderField(field, index) : renderReadOnlyField(field)
                  )}
                </div>
              )}
            </div>
          );
        })}

        <div className='brand-onboarding-result-buttons'>{actions}</div>
      </div>
    </div>
  );
};

BrandOnboardingEditor.propTypes = {
  fields: PropTypes.array.isRequired,
  isFieldEditable: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  collapsedSectionTitles: PropTypes.object.isRequired,
  onToggleSectionCollapsed: PropTypes.func.isRequired,
  recipientNamesById: PropTypes.object.isRequired,
  header: PropTypes.node,
  children: PropTypes.node,
  actions: PropTypes.node.isRequired
};

export default BrandOnboardingEditor;
